/* eslint-disable react/no-unstable-nested-components */
/* eslint-disable indent */
import './style.scss';

import { useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useMutation, useQuery } from 'react-query';
import { Link, useNavigate } from 'react-router-dom';
import { getOngoingProjects } from 'apis/project.api';
import ViewHistory from 'features/ProjectProposal/components/ViewHistory';

import { Button } from 'primereact/button';
import { Column } from 'primereact/column';
import { DataTable } from 'primereact/datatable';
import { catchError, isAuthorized, mapError } from 'utils/func';
import { Tag } from 'primereact/tag';
import {
  COUNCIL_STATUS,
  TIMEZONE_VN,
  PROJECT_ROLES,
  ORGANIZATION_TYPES,
  SUMMARY_REPORT_TYPE,
  TOAST,
} from 'constant';
import MidtermDocumentDialog from 'features/Project/components/ViewList/MidtermDocumentDialog';
import { Menu } from 'primereact/menu';
import { Badge } from 'primereact/badge';
import { statusColors } from 'utils/func/status';
import { Tooltip } from 'primereact/tooltip';
import { Dialog } from 'primereact/dialog';
import { deleteSummaryReportById } from 'apis/summaryReport';
import { toast } from 'layout';

function ProjectViewList() {
  // #region Data
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { data, isLoading, isError, error, refetch } = useQuery('project', () =>
    getOngoingProjects()
  );
  const projects = useMemo(() => data?.data?.data ?? [], [data?.data]);
  // const statuses = t('projectProposal.viewList.statuses', { returnObjects: true });
  const [viewHistoryVisible, setViewHistoryVisible] = useState(false);
  const [projectHistory, setProjectHistory] = useState([]);
  const [midtermDocumentDialogVisible, setMidtermDocumentDialogVisible] = useState(false);
  const [midtermSummaryReports, setMidtermSummaryReports] = useState([]);
  const [projectProposalData, setProjectProposalData] = useState([]);

  const [deletedSummaryReportId, setDeletedSummaryReportId] = useState(null);
  const [showDeleteSummaryReportDialog, setShowDeleteSummaryReportDialog] = useState(null);
  const { mutateAsync: deleteSummaryReportAsync, isLoading: isDeleting } =
    useMutation(deleteSummaryReportById);

  const user = JSON.parse(localStorage.getItem('user'));

  // #endregion

  // #region Format
  const formatVietnameseName = (value) => (
    <Link
      to={`/project/${value?.id}?projectProposalId=${value?.projectProposal?.id}`}
      className="hover:underline "
    >
      {value?.projectProposal?.vietnameseName}
    </Link>
  );

  const formatOwner = (value) => {
    const projectProposalContacts = value?.projectProposal?.projectProposalContacts;

    if (projectProposalContacts?.length > 0) {
      const owner = projectProposalContacts.find(
        (contact) => contact.projectRole.code === PROJECT_ROLES.OWNER
      );

      return <div>{owner?.fullname}</div>;
    }
    return null;
  };

  const formatOrganization = ({ projectProposal }) => {
    const lead = projectProposal?.projectProposalOrganizations?.find(
      ({ type }) => type === ORGANIZATION_TYPES.LEAD
    );

    return t('lang') === 'vi' ? lead?.name : lead?.englishName;
  };

  const formatDate = (date) =>
    date ? new Date(date).toLocaleDateString('en-GB', { timeZone: TIMEZONE_VN }) : '';

  const formatStartDate = (value) => formatDate(value?.startDate);

  const formatEndDate = (value) => formatDate(value?.endDate);
  const formatTotalExpenditure = (value) => {
    if (value?.totalExpenditure === 0 && value?.proposedExpenditureSummaries?.length > 0) {
      const total = value?.proposedExpenditureSummaries.reduce((sum, cur) => {
        if (cur?.proposedExpenditureType?.subProposedExpenditureTypeId === null) {
          const expenditureFromFunding = cur?.expenditureFromFunding ?? 0;
          const expenditureFromVNUHCM = cur?.expenditureFromVNUHCM ?? 0;

          return sum + expenditureFromFunding + expenditureFromVNUHCM;
        }
        return sum;
      }, 0);
      return total?.toLocaleString('vi');
    }
    return value?.totalExpenditure?.toLocaleString('vi') || 0;
  };
  // #region Render
  const formatActions = (value) => {
    const menuRef = useRef(null);

    const [
      isReviseAcceptanceReport,
      isSubmitAcceptanceReport,
      isReviseLiquidationReport,
      isSubmitLiquidationReport,
    ] = useMemo(
      () => [
        value?.acceptanceSummaryReport?.summaryReportRevises?.length > 0,
        value?.acceptanceSummaryReport?.submittedAt,
        value?.liquidationSummaryReport?.summaryReportRevises?.length > 0,
        value?.liquidationSummaryReport?.submittedAt,
      ],
      [value?.acceptanceSummaryReport, value?.liquidationSummaryReport]
    );

    const items = [];

    // Check if there's a result option available
    const hasResult =
      (value?.projectProposal?.owner?.id === user?.id ||
        isAuthorized(user?.id, value?.projectProposal?.projectProposalContacts)) &&
      value?.projectProposal?.projectProposalReviseLogs?.length > 0;

    // Check midterm documents
    const reports = value?.midtermSummaryReports ?? [];
    const isAllowedToCreate = (() => {
      if (!reports?.length) return true;
      const latestMidtermCouncilResult =
        reports[0]?.council?.projectProposalCouncils?.[0]?.councilStatus;
      return [COUNCIL_STATUS.QUALIFIED, COUNCIL_STATUS.NOT_QUALIFIED].includes(
        latestMidtermCouncilResult
      );
    })();

    // ====== GROUP: CHUNG ======
    items.push({
      label: t('project.viewList.generalGroup'),
      items: [
        {
          template: () => (
            <Link
              to={`/project-proposal/${value?.projectProposal?.id}`}
              state={{ projectId: value?.id }}
              className="p-menuitem-link"
            >
              <span className="p-menuitem-text">
                {t('project.viewList.actions.reviewProposal')}
              </span>
            </Link>
          ),
        },
        {
          template: () => (
            <Link
              to={`/project/${value?.id}?projectProposalId=${value?.projectProposal?.id}`}
              className="p-menuitem-link"
            >
              <span className="p-menuitem-text">{t('project.viewList.actions.viewContract')}</span>
            </Link>
          ),
        },
        {
          label: t('project.viewList.actions.viewHistory'),
          command: () => {
            setProjectHistory(value?.contractLogs ?? []);
            setViewHistoryVisible(true);
          },
        },
      ],
    });

    // ====== GROUP: GIỮA KỲ ======
    const midtermItems = [];
    const hasAuthorized =
      value?.projectProposal?.owner?.id === user?.id ||
      isAuthorized(user?.id, value?.projectProposal?.projectProposalContacts);

    // Create new midterm document
    if (hasAuthorized) {
      if (isAllowedToCreate) {
        midtermItems.push({
          template: () => (
            <Link
              to={`/project/${value?.id}/submit-midterm-document/create`}
              className="p-menuitem-link"
            >
              <span className="p-menuitem-text">
                {t('project.viewList.midtermDocument.submitMidtermDocument')}
              </span>
            </Link>
          ),
        });
      }
    }

    // View/Edit old midterm document
    midtermItems.push({
      label: t('project.viewList.midtermDocument.editOrViewOldMidtermDocument'),
      command: () => {
        setMidtermSummaryReports(reports);
        setProjectProposalData(value?.projectProposal);
        setMidtermDocumentDialogVisible(true);
      },
    });

    // Project adjustment
    if (hasAuthorized) {
      midtermItems.push({
        template: () => (
          <Link to={`/project-adjustment/${value?.id}`} className="p-menuitem-link">
            <span className="p-menuitem-text">
              {t('project.viewList.submitSomeDocument.title')}
            </span>
          </Link>
        ),
      });
    }

    if (midtermItems.length > 0) {
      items.push({
        label: t('project.viewList.midtermGroup'),
        items: midtermItems,
      });
    }

    // ====== GROUP: NGHIỆM THU / THANH LÝ ======
    const acceptanceLiquidationItems = [];
    const acceptanceLiquidationSummaryReportDisableTooltip = (
      <Tooltip
        target=".acceptance-liquidation-summary-report-disable"
        content={t('tooltip.onlyCreateAcceptanceReportOrLiquidationReport')}
        position="left"
      />
    );

    // Acceptance documents
    if (hasAuthorized && !value?.acceptanceSummaryReport) {
      acceptanceLiquidationItems.push({
        template: () =>
          value?.liquidationSummaryReport ? (
            <div
              className="acceptance-liquidation-summary-report-disable p-menuitem-link opacity-70"
              style={{ cursor: 'not-allowed' }}
            >
              <span className="p-menuitem-text">
                {t('project.viewList.acceptanceDocument.submit')}
              </span>
              {acceptanceLiquidationSummaryReportDisableTooltip}
            </div>
          ) : (
            <Link
              to={`/project/${value?.id}/submit-acceptance-document/create`}
              className="p-menuitem-link"
            >
              <span className="p-menuitem-text">
                {t('project.viewList.acceptanceDocument.submit')}
              </span>
            </Link>
          ),
      });
    }

    if (value?.acceptanceSummaryReport) {
      if (isSubmitAcceptanceReport) {
        acceptanceLiquidationItems.push({
          template: () => (
            <Link
              to={`/project/${value?.id}/submit-acceptance-document/detail/${value?.acceptanceSummaryReport?.id}`}
              className="p-menuitem-link"
            >
              <span className="p-menuitem-text">
                {t('project.viewList.acceptanceDocument.detail')}
              </span>
            </Link>
          ),
        });
      } else if (hasAuthorized) {
        acceptanceLiquidationItems.push({
          template: () => (
            <Link
              to={`/project/${value?.id}/submit-acceptance-document/edit/${value?.acceptanceSummaryReport?.id}`}
              className="p-menuitem-link"
            >
              <span className="p-menuitem-text">
                {t('project.viewList.acceptanceDocument.edit')}
              </span>
            </Link>
          ),
        });
      }

      if (hasAuthorized && isSubmitAcceptanceReport && isReviseAcceptanceReport) {
        acceptanceLiquidationItems.push({
          template: () => (
            <Link
              to={`/project/request/${value?.acceptanceSummaryReport?.id}`}
              className="p-menuitem-link"
            >
              <span className="p-menuitem-text">
                {t('project.viewList.acceptanceDocument.result')}
              </span>
            </Link>
          ),
        });
      }

      // Action: Delete acceptance report
      if (hasAuthorized && value?.acceptanceSummaryReport?.canDelete) {
        acceptanceLiquidationItems.push({
          template: () => (
            <div
              className="p-menuitem-link"
              onClick={() => {
                setShowDeleteSummaryReportDialog(SUMMARY_REPORT_TYPE.ACCEPTANCE_DOCUMENT);
                setDeletedSummaryReportId(value?.acceptanceSummaryReport?.id);
              }}
            >
              <span className="p-menuitem-text">
                {t('project.viewList.acceptanceDocument.delete')}
              </span>
            </div>
          ),
        });
      }
    }

    // Liquidation documents
    if (hasAuthorized && !value?.liquidationSummaryReport) {
      acceptanceLiquidationItems.push({
        template: () =>
          value?.acceptanceSummaryReport ? (
            <div
              className="acceptance-liquidation-summary-report-disable p-menuitem-link opacity-70"
              style={{ cursor: 'not-allowed' }}
            >
              <span className="p-menuitem-text">
                {t('project.viewList.liquidationDocument.submit')}
              </span>
              {acceptanceLiquidationSummaryReportDisableTooltip}
            </div>
          ) : (
            <Link
              to={`/project/${value?.id}/submit-liquidation-document/create`}
              className="p-menuitem-link"
            >
              <span className="p-menuitem-text">
                {t('project.viewList.liquidationDocument.submit')}
              </span>
            </Link>
          ),
      });
    }
    if (value?.liquidationSummaryReport) {
      if (isSubmitLiquidationReport) {
        acceptanceLiquidationItems.push({
          template: () => (
            <Link
              to={`/project/${value?.id}/submit-liquidation-document/detail/${value?.liquidationSummaryReport?.id}`}
              className="p-menuitem-link"
            >
              <span className="p-menuitem-text">
                {t('project.viewList.liquidationDocument.detail')}
              </span>
            </Link>
          ),
        });
      } else if (hasAuthorized) {
        acceptanceLiquidationItems.push({
          template: () => (
            <Link
              to={`/project/${value?.id}/submit-liquidation-document/edit/${value?.liquidationSummaryReport?.id}`}
              className="p-menuitem-link"
            >
              <span className="p-menuitem-text">
                {t('project.viewList.liquidationDocument.edit')}
              </span>
            </Link>
          ),
        });
      }

      if (hasAuthorized && isSubmitLiquidationReport && isReviseLiquidationReport) {
        acceptanceLiquidationItems.push({
          template: () => (
            <Link
              to={`/project/request/${value?.liquidationSummaryReport?.id}`}
              className="p-menuitem-link"
            >
              <span className="p-menuitem-text">
                {t('project.viewList.liquidationDocument.result')}
              </span>
            </Link>
          ),
        });
      }

      // Action: Delete liquidation report
      if (hasAuthorized && !value?.liquidationSummaryReport?.canDelete) {
        acceptanceLiquidationItems.push({
          template: () => (
            <div
              className="p-menuitem-link"
              onClick={() => {
                setShowDeleteSummaryReportDialog(SUMMARY_REPORT_TYPE.LIQUIDATION_DOCUMENT);
                setDeletedSummaryReportId(value?.liquidationSummaryReport?.id);
              }}
            >
              <span className="p-menuitem-text">
                {t('project.viewList.liquidationDocument.delete')}
              </span>
            </div>
          ),
        });
      }
    }

    // View liquidation detail
    if (value?.projectStatus?.name === 'liquidation') {
      acceptanceLiquidationItems.push({
        template: () => (
          <Link to={`/project/${value?.id}/detail/liquidation`} className="p-menuitem-link">
            <span className="p-menuitem-text">{t('project.viewList.viewLiquidation')}</span>
          </Link>
        ),
      });
    }

    if (acceptanceLiquidationItems.length > 0) {
      items.push({
        label: t('project.viewList.acceptanceLiquidationGroup'),
        items: acceptanceLiquidationItems,
      });
    }

    // ====== GROUP: HOÀN THÀNH ======
    const completionItems = [];

    // Register result
    if (
      hasAuthorized &&
      (value?.acceptanceCouncilStatus === 'qualified' ||
        value?.liquidationCouncilStatus === 'qualified') &&
      (!value?.registerResult ||
        (!!value?.registerResult && value?.registerResult?.status === 'draft'))
    ) {
      completionItems.push({
        template: () => (
          <Link
            to={`/project/${value?.id}/register-result${
              value?.liquidationCouncilStatus === 'qualified' ? '-liquidation' : ''
            }/${value?.registerResult?.id ? `edit/${value?.registerResult?.id}` : 'create'}`}
            className="p-menuitem-link"
          >
            <span className="p-menuitem-text">{t('r09.registerResult')}</span>
          </Link>
        ),
      });
    }

    if (hasAuthorized && !!value?.registerResult && value?.registerResult?.status !== 'draft') {
      completionItems.push({
        template: () => (
          <Link
            to={`/project/${value?.id}/register-result${
              value?.liquidationCouncilStatus === 'qualified' ? '-liquidation' : ''
            }/request/${value?.registerResult?.id}`}
            className="p-menuitem-link"
          >
            <div style={{ position: 'relative', display: 'flex', alignItems: 'center' }}>
              <span className="p-menuitem-text">{t('r09.resultOfRegisterResult')}</span>
              <Badge
                severity="danger"
                style={{
                  marginLeft: '8px',
                  marginTop: '-12px',
                  minWidth: '0.5rem',
                  height: '0.5rem',
                  fontSize: '0.5rem',
                  padding: '0',
                }}
              />
            </div>
          </Link>
        ),
      });
    }

    // Proposal result
    if (hasAuthorized && value?.projectProposal?.projectProposalReviseLogs?.length > 0) {
      completionItems.push({
        template: () => (
          <Link
            to={`/project-proposal/request/${value?.projectProposal?.reference}`}
            className="p-menuitem-link"
          >
            <div style={{ position: 'relative', display: 'flex', alignItems: 'center' }}>
              <span className="p-menuitem-text">{t('projectProposal.result')}</span>
              <Badge
                severity="danger"
                style={{
                  marginLeft: '8px',
                  marginTop: '-12px',
                  minWidth: '0.5rem',
                  height: '0.5rem',
                  fontSize: '0.5rem',
                  padding: '0',
                }}
              />
            </div>
          </Link>
        ),
      });
    }

    if (completionItems.length > 0) {
      items.push({
        label: t('project.viewList.completionGroup'),
        items: completionItems,
      });
    }

    return (
      <div className="flex justify-content-center">
        <Menu model={items} popup ref={menuRef} style={{ minWidth: '250px' }} />
        <div style={{ position: 'relative', display: 'inline-block' }}>
          <Button
            icon="pi pi-ellipsis-v"
            className="p-button-rounded p-button-text"
            onClick={(event) => menuRef.current.toggle(event)}
          />
          {hasResult && (
            <Badge
              severity="danger"
              style={{
                position: 'absolute',
                top: '-2px',
                right: '-2px',
                minWidth: '0.5rem',
                height: '0.5rem',
                fontSize: '0.5rem',
                padding: '0',
              }}
            />
          )}
        </div>
      </div>
    );
  };

  const formatStatus = (value) => {
    const status = value.dashboardStatus;

    return (
      <Tag
        style={{
          maxWidth: '8rem',
          backgroundColor: statusColors.submit[status],
          color: '#ffffff',
        }}
        value={t(`projectNewStatuses.${status}`)}
        rounded
      />
    );
  };

  const onConfirmDeleteSummaryReport = async () => {
    try {
      await deleteSummaryReportAsync(deletedSummaryReportId);
      setShowDeleteSummaryReportDialog(null);
      setDeletedSummaryReportId(null);
      refetch();
      toast(
        TOAST.SUCCESS,
        t(`project.viewList.${showDeleteSummaryReportDialog}.deleteSuccess`),
        2000
      );
    } catch (deleteError) {
      catchError(t, deleteError, navigate);
    }
  };

  const footerConfirmationPrompt = () => (
    <div>
      <Button
        label={t('formLayout.action.cancel')}
        className="w-10rem"
        severity="danger"
        onClick={() => setShowDeleteSummaryReportDialog(null)}
        autoFocus
      />
      <Button
        label={t('formLayout.action.ok')}
        className="w-10rem"
        severity="info"
        onClick={() => onConfirmDeleteSummaryReport()}
        loading={isDeleting}
      />
    </div>
  );

  return (
    <div className="grid">
      <div className="col-12">
        <div className="card">
          {isError && <h3 className="text-red-600">{mapError(t, error)}</h3>}
          <div className="flex justify-content-between align-items-end mb-4 pb-2">
            <h5 className="p-0 m-0">{t('project.viewList.title')}</h5>
          </div>

          <DataTable
            value={projects}
            className="p-datatable-gridlines header-table"
            showGridlines
            dataKey="id"
            filterDisplay="menu"
            header=""
            scrollable
            loading={isLoading}
            // responsiveLayout="scroll"
            emptyMessage={t('projectProposal.viewList.emptyMessage')}
          >
            <Column
              field="projectProposal.vietnameseName"
              body={formatVietnameseName}
              header={t('project.viewList.vietnameseName')}
              style={{
                minWidth: '12rem',
                maxWidth: '16rem',
                textAlign: 'left',
              }}
              frozen
              alignFrozen="left"
            />
            <Column
              header={t('project.viewList.owner')}
              style={{ minWidth: '8rem', textAlign: 'center' }}
              body={formatOwner}
            />
            <Column
              header={t('project.viewList.loadOrganization')}
              style={{ minWidth: '8rem', textAlign: 'center' }}
              body={formatOrganization}
            />
            <Column
              header={t('project.viewList.totalExpenditure')}
              style={{ minWidth: '8rem', textAlign: 'center' }}
              body={formatTotalExpenditure}
            />
            <Column
              header={t('project.viewList.startDate')}
              style={{ minWidth: '8rem', textAlign: 'center' }}
              body={formatStartDate}
            />
            <Column
              header={t('project.viewList.endDate')}
              style={{ minWidth: '8rem', textAlign: 'center' }}
              body={formatEndDate}
            />
            <Column
              field="projectStatus.name"
              header={t('project.viewList.status')}
              style={{ minWidth: '8rem', textAlign: 'center' }}
              body={formatStatus}
            />
            <Column
              header={t('project.action')}
              body={formatActions}
              style={{ minWidth: '6rem', maxWidth: '16rem', textAlign: 'center' }}
              frozen
              alignFrozen="right"
            />
          </DataTable>
          <ViewHistory
            data={projectHistory}
            visible={viewHistoryVisible}
            setVisible={setViewHistoryVisible}
            title={t('project.viewHistory.title')}
          />
          <MidtermDocumentDialog
            midtermDocuments={midtermSummaryReports}
            proposalData={projectProposalData}
            visible={midtermDocumentDialogVisible}
            setVisible={setMidtermDocumentDialogVisible}
            refetch={refetch}
          />

          <Dialog
            visible={!!showDeleteSummaryReportDialog}
            header={
              // eslint-disable-next-line react/jsx-wrap-multilines
              <p className="text-blue-500">
                {t(`project.viewList.${showDeleteSummaryReportDialog}.delete`)}
              </p>
            }
            position="center"
            style={{ width: '400px' }}
            onHide={() => {
              setShowDeleteSummaryReportDialog(null);
            }}
            draggable={false}
            footer={footerConfirmationPrompt}
          >
            <p className="text-lg pb-3">
              {t(`project.viewList.${showDeleteSummaryReportDialog}.deleteConfirmation`)}
            </p>
          </Dialog>
        </div>
      </div>
    </div>
  );
}

export default ProjectViewList;
